/**
 * styles.js — 홍보물원가 관리 페이지 공통 스타일 상수 + SortIcon 헬퍼
 *
 * Named exports:
 *   cardStyle, cardHeaderStyle, cardTitleStyle, inlineFormStyle,
 *   fieldGroup, fieldLabel, inputStyle,
 *   tableStyle, thStyle, tdStyle, trStyle,
 *   addBtnStyle, saveBtnStyle, cancelBtnStyle, editBtnStyle, historyBtnStyle
 *   SortIcon
 */
import React from 'react';
// 2026-08-27 디자인 통일: 직원 관리 기준 (designStandard.js) — 본문 그림자 제거·1px 테두리·검정 머리글 띠
import { stdBlackBtn, stdOutlineBtn } from '../adminlayout/designStandard';

// ── 카드 컨테이너 ──────────────────────────────────────────────────────────────
// 각 섹션을 감싸는 흰색 카드 박스 (디자인 표준: 라운드 8·그림자 없음)
export const cardStyle = {
  background: 'var(--bg-card)',
  borderRadius: 8,
  boxShadow: 'none',
  border: '1px solid var(--border)',
  overflow: 'hidden',
};

// 카드 상단 헤더 (제목 + 추가 버튼 행)
export const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 20px',
  borderBottom: '1px solid var(--bg-subtle)',
};

// 카드 제목 텍스트
export const cardTitleStyle = {
  fontSize: 15,
  fontWeight: 800,
  color: 'var(--text-sub)',
};

// ── 인라인 폼 ─────────────────────────────────────────────────────────────────
// 테이블 상단에 나타나는 신규/수정 인라인 입력 영역
export const inlineFormStyle = {
  padding: '12px 20px',
  background: 'var(--bg-subtle)',
  borderBottom: '1px solid var(--border)',
};

// 라벨+입력 한 세트를 세로로 묶는 컨테이너
export const fieldGroup = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minWidth: 130,
};

// 입력 필드 위 라벨
export const fieldLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-muted)',
};

// 공통 input/select 스타일
export const inputStyle = {
  padding: '5px 8px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  background: 'var(--bg-card)',
};

// ── 테이블 ────────────────────────────────────────────────────────────────────
// 풀너비 collapse 테이블
export const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

// 테이블 헤더 셀 — 디자인 표준: 검정 머리글 띠 (stdTh 톤)
export const thStyle = {
  padding: '10px 12px',
  background: 'var(--std-thead, #0f172a)',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.92)',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderBottom: 'none',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// 테이블 데이터 셀
export const tdStyle = {
  padding: '9px 12px',
  borderBottom: '1px solid var(--bg-subtle)',
  verticalAlign: 'middle',
};

// 테이블 행 (hover 배경 전환)
export const trStyle = {
  transition: 'background 0.1s',
};

// ── 버튼 ──────────────────────────────────────────────────────────────────────
// 항목 추가 버튼 — 각 섹션의 주요 액션: 검정 박스 (디자인 표준)
export const addBtnStyle = { ...stdBlackBtn };

// 저장 버튼 — 주요 액션: 검정 박스 (디자인 표준)
export const saveBtnStyle = { ...stdBlackBtn };

// 취소 버튼 — 보조 액션: 흰 외곽선 (디자인 표준)
export const cancelBtnStyle = { ...stdOutlineBtn };

// 수정 버튼 — 표 안 축소형 중립 외곽선 (직원 관리 tableActionBtn 톤)
export const editBtnStyle = {
  fontSize: 11,
  padding: '3px 10px',
  background: 'var(--bg-card)',
  color: 'var(--text-sub)',
  border: '1px solid var(--border)',
  borderRadius: 5,
  cursor: 'pointer',
  fontWeight: 700,
};

// 이력 accordion 토글 버튼 — 중립 외곽선
export const historyBtnStyle = {
  fontSize: 11,
  padding: '3px 10px',
  border: '1px solid var(--border)',
  borderRadius: 5,
  cursor: 'pointer',
  fontWeight: 700,
  color: 'var(--text-sub)',
};

// ── SortIcon 헬퍼 컴포넌트 ────────────────────────────────────────────────────
/**
 * 테이블 헤더 옆에 표시되는 정렬 방향 아이콘
 * @param {{ current: {key:string, dir:string}, colKey: string }} props
 */
export function SortIcon({ current, colKey }) {
  // 현재 열이 정렬 기준 열인지 확인
  const active = current.key === colKey;
  return (
    <span
      style={{
        marginLeft: 4,
        fontSize: 10,
        // 검정 머리글 띠 위: 활성 열은 흰색, 비활성 열은 반투명 흰색
        color: active ? '#fff' : 'rgba(255,255,255,0.45)',
        verticalAlign: 'middle',
      }}
    >
      {/* 활성: 방향에 따라 ▲/▼, 비활성: 양방향 화살표 */}
      {active ? (current.dir === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );
}
